import { useEffect } from 'react'
import type { AbstractAgent } from '@copilotkit/react-core/v2'
import useNotification from '@editor/hooks/useNotification'

/**
 * A system notification when the agent finishes a turn while the tab is
 * not focused -- with its answer, or waiting for the user's approval -- like
 * a finished calculation or document. Only with notifications enabled in the
 * app (`useNotification`). Turns replayed on reconnect carry their earlier
 * run id, not the one this client sent (`createLiveRunTracker`),
 * and are skipped; so is a turn the user stopped.
 *
 * A subscription to the agent's event stream: the outside world this effect
 * exists for.
 */
const useResponseNotifications = (agent: AbstractAgent): void => {
  const { triggerNotification } = useNotification()

  useEffect(() => {
    const subscription = agent.subscribe({
      onRunFinishedEvent: params => {
        if (params.event.runId !== params.input.runId) return
        if (params.outcome === 'cancelled') return
        triggerNotification({
          message:
            params.outcome === 'interrupt'
              ? 'KI-Chat: Der Assistent wartet auf Ihre Entscheidung.'
              : 'KI-Chat: Der Assistent hat geantwortet.',
        })
      },
    })
    return () => subscription.unsubscribe()
  }, [agent, triggerNotification])
}

export default useResponseNotifications
